import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
    ActionIcon,
    Badge,
    Box,
    Divider,
    Group,
    rem,
    Text,
    Tooltip,
} from "@mantine/core";
import { SVGProps } from "react";
import { AppliedFilterInfo } from "../../../models/person.model";

const IconInformationCircle = (props: SVGProps<SVGSVGElement>) => (
    <InformationCircleIcon {...props} />
);

interface SearchMatchedBadgesProps {
    appliedFilters: AppliedFilterInfo;
    /** Called when a badge is removed. Keyword is the original search term typed by the user. */
    onRemoveBadge?: (type: "tech" | "cert", keyword: string) => void;
}

export function SearchMatchedBadges({
    appliedFilters,
    onRemoveBadge,
}: SearchMatchedBadgesProps) {
    const hasTech = Object.keys(appliedFilters.matchedTechnologies).length > 0;
    const hasCerts = Object.keys(appliedFilters.matchedCertificates).length > 0;

    if (!hasTech && !hasCerts) return null;

    return (
        <Group gap="xs" align="center" wrap="wrap">
            <Divider orientation="vertical" />

            <Text size="sm" c="dimmed">
                Search matched:
            </Text>

            <Tooltip
                label={
                    <Box>
                        <Text size="sm" fw={500} mb="xs">
                            Fuzzy Search Results
                        </Text>
                        <Text size="xs">
                            Our intelligent search uses fuzzy matching to find similar terms.
                        </Text>
                        <Text size="xs" mt="xs">
                            •{" "}
                            <Text component="span" fw={500}>
                                Green badges
                            </Text>
                            : Exact matches
                        </Text>
                        <Text size="xs">
                            •{" "}
                            <Text component="span" fw={500}>
                                Blue/Orange badges
                            </Text>
                            : Similar terms found
                        </Text>
                        <Text size="xs" mt="xs" c="dimmed">
                            Example: Searching "Reactjs" might also find "ReactJS", "React.js"
                        </Text>
                    </Box>
                }
                position="bottom"
                withArrow
                multiline
                w={300}
            >
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    style={{ cursor: "help" }}
                    aria-label="Fuzzy search explanation"
                >
                    <IconInformationCircle style={{ width: rem(16), height: rem(16) }} />
                </ActionIcon>
            </Tooltip>

            {hasTech && (
                <>
                    <Divider orientation="vertical" />
                    <Box style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                        <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                            Tech:
                        </Text>
                        {Object.entries(appliedFilters.matchedTechnologies).flatMap(
                            ([original, matchedList]) =>
                                matchedList.map((matched, idx) => {
                                    const originalStr = String(original || "");
                                    const matchedStr = String(matched || "");
                                    return (
                                        <Badge
                                            key={`tech-${originalStr}-${idx}`}
                                            size="sm"
                                            variant="light"
                                            color={
                                                originalStr.toLowerCase() === matchedStr.toLowerCase()
                                                    ? "green"
                                                    : "blue"
                                            }
                                            style={onRemoveBadge ? { cursor: "pointer" } : undefined}
                                            onClick={
                                                onRemoveBadge
                                                    ? () => onRemoveBadge("tech", originalStr)
                                                    : undefined
                                            }
                                        >
                                            {originalStr !== matchedStr
                                                ? `${originalStr} → ${matchedStr}`
                                                : matchedStr}
                                        </Badge>
                                    );
                                })
                        )}
                    </Box>
                </>
            )}

            {hasCerts && (
                <>
                    <Divider orientation="vertical" />
                    <Box style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                        <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                            Certs:
                        </Text>
                        {Object.entries(appliedFilters.matchedCertificates).flatMap(
                            ([original, matchedList]) =>
                                matchedList.map((matched, idx) => {
                                    const originalStr = String(original || "");
                                    const matchedStr = String(matched || "");
                                    return (
                                        <Badge
                                            key={`cert-${originalStr}-${idx}`}
                                            size="sm"
                                            variant="light"
                                            color="orange"
                                            style={onRemoveBadge ? { cursor: "pointer" } : undefined}
                                            onClick={
                                                onRemoveBadge
                                                    ? () => onRemoveBadge("cert", originalStr)
                                                    : undefined
                                            }
                                        >
                                            {originalStr !== matchedStr
                                                ? `${originalStr} → ${matchedStr}`
                                                : matchedStr}
                                        </Badge>
                                    );
                                })
                        )}
                    </Box>
                </>
            )}
        </Group>
    );
}
